<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">才艺视频审核详情</h2>
      <el-button @click="goBack">返回列表</el-button>
    </div>

    <el-descriptions :column="2" border>
      <el-descriptions-item label="视频标题">{{ displayValue(getBaseInfo('title')) }}</el-descriptions-item>
      <el-descriptions-item label="审核状态">
        <el-tag :type="statusTagType(currentStatus)">{{ statusText(currentStatus) }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="艺人">{{ displayValue(getBaseInfo('artistName')) }}</el-descriptions-item>
      <el-descriptions-item label="手机号">{{ displayValue(getBaseInfo('mobile')) }}</el-descriptions-item>
      <el-descriptions-item label="分类">{{ displayValue(getBaseInfo('taxonomyName')) }}</el-descriptions-item>
      <el-descriptions-item label="提交时间">{{ displayValue(getBaseInfo('createdAt')) }}</el-descriptions-item>
      <el-descriptions-item label="驳回原因" :span="2">
        <div class="pre-line">{{ displayValue(getBaseInfo('rejectReason')) }}</div>
      </el-descriptions-item>
    </el-descriptions>

    <el-divider>视频预览</el-divider>
    <div class="video-preview-wrap">
      <video
        v-if="videoUrl"
        class="video-preview"
        :src="videoUrl"
        :poster="coverUrl"
        controls
        preload="metadata"
      />
      <el-empty v-else description="暂无视频" :image-size="88" />
    </div>

    <el-divider>审核记录</el-divider>
    <el-table :data="detail.auditHistory || []" border>
      <el-table-column label="状态" width="140">
        <template slot-scope="{ row }">{{ statusText(row.status) }}</template>
      </el-table-column>
      <el-table-column prop="reason" label="原因" />
      <el-table-column prop="operatorName" label="处理人" width="160" />
      <el-table-column prop="operatedAt" label="处理时间" width="180" />
    </el-table>

    <div v-if="canOperate" class="action-bar">
      <el-button v-if="canApprove" type="success" :loading="submitting" @click="approve">审核通过</el-button>
      <el-button v-if="canReject" type="danger" :loading="submitting" @click="openRejectDialog">驳回</el-button>
    </div>

    <el-dialog title="驳回原因" :visible.sync="rejectDialogVisible" width="480px" @closed="resetRejectDialog">
      <el-input v-model.trim="rejectReason" type="textarea" :rows="4" placeholder="请输入驳回原因" />
      <span slot="footer">
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="reject">确认驳回</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  approveArtistTalentVideo,
  getArtistTalentVideoAuditDetail,
  rejectArtistTalentVideo
} from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'
import { hasPermission } from '../../utils/auth'
import { resolveMediaUrl } from '../../utils/format'

const DEFAULT_STATUS = 1
const DEFAULT_PAGE = 1
const AUDIT_STATUS_PENDING = 1
const AUDIT_STATUS_APPROVED = 2
const AUDIT_STATUS_REJECTED = 3

export default {
  name: 'ArtistTalentVideoAuditDetailPage',
  data() {
    return {
      detail: {},
      rejectDialogVisible: false,
      rejectReason: '',
      submitting: false
    }
  },
  computed: {
    currentStatus() {
      return Number(this.getBaseInfo('status')) || 0
    },
    canOperate() {
      return this.currentStatus === AUDIT_STATUS_PENDING
    },
    canApprove() {
      return hasPermission('artist_talent_video_audit:approve')
    },
    canReject() {
      return hasPermission('artist_talent_video_audit:reject')
    },
    videoUrl() {
      return resolveMediaUrl(this.getBaseInfo('videoUrl'))
    },
    coverUrl() {
      return resolveMediaUrl(this.getBaseInfo('coverUrl'))
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    getBaseInfo(key) {
      return this.detail.baseInfo ? this.detail.baseInfo[key] : ''
    },
    displayValue(value) {
      return value === 0 || value ? String(value) : '-'
    },
    statusText(status) {
      if (status === AUDIT_STATUS_PENDING) return '待审核'
      if (status === AUDIT_STATUS_APPROVED) return '已通过'
      if (status === AUDIT_STATUS_REJECTED) return '已驳回'
      return status == null ? '-' : String(status)
    },
    statusTagType(status) {
      if (status === AUDIT_STATUS_PENDING) return 'warning'
      if (status === AUDIT_STATUS_APPROVED) return 'success'
      if (status === AUDIT_STATUS_REJECTED) return 'danger'
      return 'info'
    },
    goBack() {
      const query = this.$route.query || {}
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: query.fromType || 'artist-talent-video',
          status: query.fromStatus || String(DEFAULT_STATUS),
          page: query.fromPage || String(DEFAULT_PAGE)
        }
      })
    },
    async fetchData() {
      try {
        this.detail = await getArtistTalentVideoAuditDetail(this.$route.params.id)
      } catch (error) {
        this.$message.error(error.message || '加载才艺视频详情失败')
      }
    },
    async approve() {
      if (this.submitting) return
      try {
        await MessageBox.confirm('确认审核通过该才艺视频吗？', '审核确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        await approveArtistTalentVideo(this.$route.params.id, { remark: '审核通过' })
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '审核通过失败')
      } finally {
        this.submitting = false
      }
    },
    openRejectDialog() {
      this.rejectDialogVisible = true
    },
    resetRejectDialog() {
      this.rejectReason = ''
    },
    async reject() {
      if (!this.rejectReason) {
        this.$message.warning('请填写驳回原因')
        return
      }
      try {
        this.submitting = true
        await rejectArtistTalentVideo(this.$route.params.id, {
          reason: this.rejectReason,
          remark: this.rejectReason
        })
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '驳回失败')
      } finally {
        this.submitting = false
        this.rejectDialogVisible = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.video-preview-wrap {
  width: 360px;
  max-width: 100%;
}

.video-preview {
  width: 360px;
  max-width: 100%;
  height: 640px;
  max-height: 70vh;
  background: #111827;
  border-radius: 8px;
}

.pre-line {
  white-space: pre-wrap;
}

.action-bar {
  margin-top: 20px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
